import React from "react";
import { connect } from "react-redux";
// @material-ui/core components
import withStyles from "@material-ui/core/styles/withStyles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
// @material-ui/icons
import Close from "@material-ui/icons/Close";
// core components
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";

import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle.jsx";
import { enrichMediaFromUrl } from "../media-library-api.js";
import {
  isValidHttpUrl
} from "../source-url-allowlist.js";

// The fields video-api enforces on commit (relaxed in fdc0a68 to just these
// three) and returns in a 422 { mandatoryMissing }. Save stays disabled while
// any is empty. URL first, matching the new CMS.
const MANDATORY = ["source_url", "title", "description"];

// Always-visible core fields; everything else lives in the collapsed
// "Additional details" section (mirrors the new-CMS MediaReviewDialog).
// Source is always visible, not tucked behind "Additional details": it is
// auto-filled and usually has one valid value, but for Getty video it is a real
// choice — and a choice hidden behind a collapsed section is a choice nobody makes.
const CORE_KEYS = { source_url: true, title: true, description: true, source_label: true };

// The editable field set. `key` maps to the flat analyze `fields` map;
// `required` marks a core gating field; `area` renders a textarea; `wide`
// renders full-width; `readOnly` is display-only. Order matters: the 3 core
// fields (URL, Title, Description) render first + always visible; the rest
// render inside the collapsed "Additional details" section — all optional and
// always rendered (matches the new CMS).
const FIELD_DEFS = [
  { key: "source_url", label: "URL", required: true },
  { key: "title", label: "Title", required: true },
  { key: "description", label: "Description", required: true, area: true },
  { key: "credit_line", label: "Credit line" },
  { key: "copyright_notice", label: "Copyright notice" },
  { key: "file_name", label: "File name" },
  { key: "source_label", label: "Source", readOnly: true, wide: true },
  { key: "photographer", label: "Photographer" },
  { key: "keywords", label: "Keywords", wide: true },
  { key: "city", label: "City" },
  { key: "country", label: "Country" },
  { key: "captured_at", label: "Captured at" },
  { key: "uploaded_by", label: "Uploaded by", readOnly: true }
];

function isBlank(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  return false;
}

// Media preview from the analyze `preview` descriptor. `preview` may be a bare
// URL string or an object with { url, type } — handle both defensively.
function Preview(props) {
  const preview = props.preview;
  const mediaType = props.mediaType;
  if (!preview) return null;
  // Backend analyze preview descriptor (video-api preview.js): images →
  // { type:'image', imageUrl }, video → { type:'video', posterUrl }. Read those
  // keys (url/src were never emitted, so no preview ever rendered).
  const url =
    typeof preview === "string"
      ? preview
      : preview.imageUrl || preview.posterUrl || preview.url || preview.src;
  if (!url) return null;
  const kind = (preview && preview.type) || mediaType || "image";

  const boxStyle = { textAlign: "center", marginBottom: 16 };
  if (kind === "video") {
    return (
      <div style={boxStyle}>
        <video
          src={url}
          controls
          style={{ maxWidth: "100%", maxHeight: 280 }}
        />
      </div>
    );
  }
  if (kind === "audio") {
    return (
      <div style={boxStyle}>
        <audio src={url} controls style={{ width: "100%" }} />
      </div>
    );
  }
  return (
    <div style={boxStyle}>
      <img
        src={url}
        alt="preview"
        style={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain" }}
      />
    </div>
  );
}

class ReviewDialog extends React.Component {
  constructor(props) {
    super(props);
    // Seed the editable form from the flat `fields` map. Keys may be missing;
    // default to "" so every rendered input is controlled.
    const fields =
      props.analysis && props.analysis.fields ? props.analysis.fields : {};
    const form = {};
    FIELD_DEFS.forEach(function(def) {
      const v = fields[def.key];
      // keywords may come back as an array — flatten to a comma list for editing.
      form[def.key] = Array.isArray(v)
        ? v.join(", ")
        : v === null || v === undefined
          ? ""
          : String(v);
    });
    this.state = {
      form: form,
      // mandatoryMissing highlighted after a 422 from commit.
      serverMissing: [],
      fetching: false,
      // Source-label choices, refreshed when the editor fetches from the URL
      // (which is normally when the provider first becomes known). Seeded empty;
      // sourceLabelOptions() falls back to the analyze result.
      labelOptions: [],
      // "Additional details" section starts collapsed (matches the new CMS).
      showMore: false
    };
  }

  // Scrape the entered Source URL (Getty/AP article/etc.) and fill blank fields
  // only — never clobber what the file or the user already provided.
  handleFetchUrl = () => {
    const self = this;
    const url = (this.state.form.source_url || "").trim();
    if (!url || this.state.fetching) return;
    this.setState({ fetching: true });
    var analysisForType = this.props.analysis || {};
    enrichMediaFromUrl(
      this.props.activeDomain, url,
      this.state.form.source || undefined,
      analysisForType.mediaType
    )
      .then(function(res) {
        const fields = (res && res.fields) || {};
        const opts = (res && res.sourceLabelOptions) || [];
        self.setState(function(prev) {
          const form = Object.assign({}, prev.form);
          let filledNonCore = false;
          Object.keys(fields).forEach(function(k) {
            const v = fields[k];
            if (v && !(form[k] && String(form[k]).trim())) {
              form[k] = String(v);
              if (!CORE_KEYS[k]) filledNonCore = true;
            }
          });
          // The label is REPLACED, not filled-blank: the pasted URL is
          // authoritative about which provider licensed this, so it overrides a
          // weaker guess made from the filename or IPTC before the URL was known.
          if (opts.length && opts.indexOf(form.source_label) < 0) {
            form.source_label = (res && res.sourceLabel) || opts[0];
          }
          const nextMissing = prev.serverMissing.filter(function(k) {
            return !(form[k] && String(form[k]).trim());
          });
          // Auto-open "Additional details" if enrichment filled an optional
          // field (e.g. a scraped Getty credit_line) so it's never hidden.
          return {
            form: form, serverMissing: nextMissing, fetching: false,
            labelOptions: opts.length ? opts : prev.labelOptions,
            showMore: prev.showMore || filledNonCore
          };
        });
      })
      .catch(function() { self.setState({ fetching: false }); });
  };

  handleChange = key => event => {
    const value = event.target.value;
    const self = this;
    this.setState(function(prev) {
      const nextForm = Object.assign({}, prev.form);
      nextForm[key] = value;
      // Clear the server-side "missing" flag for a field once it's edited.
      const nextMissing = prev.serverMissing.filter(function(k) {
        return k !== key;
      });
      return { form: nextForm, serverMissing: nextMissing };
    }, function() {
      // Auto-fetch details when a provider URL is entered/pasted, so "add the URL"
      // fills the fields without a click. Debounced, once per URL, blanks only.
      if (key === "source_url") {
        var url = String(value || "").trim();
        clearTimeout(self._autoFetchTimer);
        var f = self.state.form;
        var needsFill = !(f.title && String(f.title).trim()) || !(f.description && String(f.description).trim()) || !(f.credit_line && String(f.credit_line).trim());
        // Any valid http URL auto-fetches. A disallowed provider yields nothing to fill,
        // but the enrich call reaches the server, which rejects it and logs the attempt.
        if (/^https?:\/\//i.test(url) && url !== self._autoFetchUrl && needsFill) {
          self._autoFetchTimer = setTimeout(function() { self._autoFetchUrl = url; self.handleFetchUrl(); }, 800);
        }
      }
    });
  };

  coreFields = () => {
    return FIELD_DEFS.filter(function(def) { return CORE_KEYS[def.key]; });
  };

  moreFields = () => {
    return FIELD_DEFS.filter(function(def) { return !CORE_KEYS[def.key]; });
  };

  // One field row (CustomInput). Reused for both the core zone and the
  // collapsed "Additional details" zone. Keeps the source_url "Fetch details"
  // button and the read-only rendering for uploaded_by.
  renderField = def => {
    const self = this;
    const requiredEmpty = def.required && this.missingMandatory().indexOf(def.key) >= 0;
    const urlIssue = def.key === "source_url" ? this.urlIssue() : null;
    const isError = this.state.serverMissing.indexOf(def.key) >= 0 || requiredEmpty || !!urlIssue;
    // Fetch just needs a syntactically valid URL; the provider check is server-side.
    const urlInvalidForFetch = !isValidHttpUrl(self.state.form.source_url);
    var labelOptions = this.sourceLabelOptions();
    if (def.key === "source_label" && labelOptions.length > 1) {
      return (
        <GridItem xs={12} md={def.area || def.wide ? 12 : 6} key={def.key}>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, color: "#AAAAAA", marginBottom: 4 }}>{def.label}</div>
            <select
              id="media-source_label"
              value={self.state.form.source_label || labelOptions[0]}
              onChange={self.handleChange(def.key)}
              style={{
                width: "100%", padding: "6px 0", fontSize: 14,
                border: "none", borderBottom: "1px solid #D2D2D2", background: "transparent"
              }}
            >
              {labelOptions.map(function(opt) {
                return <option key={opt} value={opt}>{opt}</option>;
              })}
            </select>
          </div>
        </GridItem>
      );
    }
    return (
      <GridItem xs={12} md={def.area || def.wide ? 12 : 6} key={def.key}>
        <CustomInput
          id={"media-" + def.key}
          labelText={def.label + (def.required ? " *" : "")}
          error={isError}
          formControlProps={{ fullWidth: true }}
          value={self.state.form[def.key]}
          inputProps={{
            value: self.state.form[def.key],
            onChange: self.handleChange(def.key),
            multiline: !!def.area,
            rowsMax: def.area ? 6 : undefined,
            readOnly: !!def.readOnly
          }}
        />
        {def.key === "source_url" ? (
          <Button
            size="sm"
            color="rose"
            simple
            disabled={self.state.fetching || urlInvalidForFetch}
            onClick={self.handleFetchUrl}
          >
            {self.state.fetching ? "Fetching…" : "Fetch details from URL"}
          </Button>
        ) : null}
        {def.key === "source_url" && urlIssue === "format" ? (
          <div style={{ fontSize: 12, marginTop: 4, color: "#f44336" }}>
            Enter a valid URL (must start with http:// or https://).
          </div>
        ) : null}
      </GridItem>
    );
  };

  missingMandatory = () => {
    const form = this.state.form;
    return MANDATORY.filter(function(k) {
      return isBlank(form[k]);
    });
  };

  // Classify the entered source_url: null = fine (or empty, which is handled by
  // missingMandatory), "format" = not a valid http(s) URL (blocks junk like
  // "Nil"), "provider" = valid URL but not an approved provider. Mirrors the
  // backend allowlist so a bad URL is caught before Save/commit.
  urlIssue = () => {
    const raw = (this.state.form.source_url || "").trim();
    if (raw === "") return null;
    if (!isValidHttpUrl(raw)) return "format";
    return null;
  };

  // Server-supplied Source choices for this (provider, media type). Length > 1
  // means the editor picks — today only Getty video. The vocabulary is never
  // hard-coded here, so adding a provider stays a backend-only change.
  sourceLabelOptions = () => {
    if (this.state.labelOptions && this.state.labelOptions.length) return this.state.labelOptions;
    var a = this.props.analysis || {};
    return a.sourceLabelOptions || [];
  };

  // A determinate refusal from analyze (provider conflict / unrecognised file
  // type). Commit re-checks server-side; this stops the editor filling in a form
  // that can never be saved.
  blocked = () => {
    var a = this.props.analysis || {};
    return a.blocked || null;
  };

  saveDisabled = () => {
    if (this.props.saving) return true;
    if (this.blocked()) return true;
    if (this.missingMandatory().length > 0) return true;
    // Require a syntactically valid URL only; the provider restriction is enforced (and
    // logged) server-side, so a disallowed URL reaches commit and is rejected there.
    if (!isValidHttpUrl(this.state.form.source_url)) return true;
    return false;
  };

  buildFields = () => {
    // Send back the full edited map. keywords -> array for the backend.
    const form = this.state.form;
    const out = {};
    Object.keys(form).forEach(function(k) {
      if (k === "keywords") {
        out[k] = form[k]
          .split(",")
          .map(function(s) {
            return s.trim();
          })
          .filter(Boolean);
      } else {
        out[k] = form[k];
      }
    });
    return out;
  };

  handleSave = (duplicateDecision, duplicateId) => () => {
    const payload = { fields: this.buildFields() };
    if (duplicateDecision) {
      payload.duplicateDecision = duplicateDecision;
      if (duplicateId) payload.duplicateId = duplicateId;
    }
    // onSave returns a promise; a rejected { mandatoryMissing } highlights fields.
    const self = this;
    Promise.resolve(this.props.onSave(payload)).catch(function(err) {
      if (err && err.mandatoryMissing) {
        self.setState({ serverMissing: err.mandatoryMissing });
      }
    });
  };

  renderDuplicates = () => {
    const duplicates =
      (this.props.analysis && this.props.analysis.duplicates) || [];
    if (!duplicates.length) return null;
    const top = duplicates[0];
    return (
      <div
        style={{
          border: "1px solid #ffcc80",
          background: "#fff8e1",
          borderRadius: 6,
          padding: 12,
          marginBottom: 16
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          Possible duplicate
          {duplicates.length > 1 ? "s" : ""} found
        </div>
        {duplicates.map(function(d) {
          return (
            <div
              key={d.publicId}
              style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
            >
              {d.thumb ? (
                <img
                  src={d.thumb}
                  alt=""
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 4,
                    marginRight: 10
                  }}
                />
              ) : null}
              <div style={{ fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{d.title || d.publicId}</div>
                <div style={{ color: "#888" }}>
                  {d.tier ? "tier " + d.tier : ""}
                  {typeof d.distance === "number"
                    ? " · distance " + d.distance
                    : ""}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ marginTop: 8 }}>
          <Button
            color="info"
            size="sm"
            onClick={this.handleSave("use_existing", top.publicId)}
            disabled={this.props.saving}
          >
            Use existing
          </Button>
          <Button
            color="rose"
            size="sm"
            onClick={this.handleSave("keep_both", top.publicId)}
            disabled={this.saveDisabled()}
          >
            Keep both
          </Button>
          <Button
            size="sm"
            onClick={this.handleSave("save_anyway", top.publicId)}
            disabled={this.saveDisabled()}
          >
            Save anyway
          </Button>
        </div>
      </div>
    );
  };

  render() {
    const classes = this.props.classes;
    const analysis = this.props.analysis || {};
    const missing = this.missingMandatory();
    const self = this;

    return (
      <Dialog
        classes={{ root: classes.center, paper: classes.modal }}
        open={this.props.open}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ style: { maxHeight: "92vh", display: "flex", flexDirection: "column" } }}
        aria-labelledby="media-review-title"
      >
        <DialogTitle
          id="media-review-title"
          disableTypography
          className={classes.modalHeader}
        >
          <Button
            justIcon
            className={classes.modalCloseButton}
            key="close"
            aria-label="Close"
            color="transparent"
            onClick={this.props.onCancel}
          >
            <Close className={classes.modalClose} />
          </Button>
          <h4 className={classes.modalTitle}>Review &amp; Save</h4>
        </DialogTitle>

        <DialogContent
          id="media-review-body"
          className={classes.modalBody}
          style={{ overflowY: "auto", overflowX: "hidden", flex: "1 1 auto", minHeight: 0 }}
        >
          <Preview preview={analysis.preview} mediaType={analysis.mediaType} />

          {this.renderDuplicates()}

          <GridContainer>
            {this.blocked() ? (
              <GridItem xs={12} key="media-blocked">
                <div style={{
                  border: "1px solid #f44336", background: "#fdecea",
                  borderRadius: 4, padding: 12, marginBottom: 8
                }}>
                  <div style={{ color: "#c62828", fontWeight: 500, fontSize: 14 }}>
                    This file can&#39;t be added
                  </div>
                  <div style={{ color: "#555555", fontSize: 13, marginTop: 2 }}>
                    {this.blocked().message}
                  </div>
                </div>
              </GridItem>
            ) : null}
            {this.coreFields().map(this.renderField)}
          </GridContainer>

          <div style={{ borderTop: "1px solid #eee", marginTop: 8, paddingTop: 4 }}>
            <Button
              size="sm"
              simple
              color="rose"
              onClick={function() { self.setState({ showMore: !self.state.showMore }); }}
            >
              {this.state.showMore ? "Hide additional details" : "Additional details"}
            </Button>
          </div>
          {this.state.showMore ? (
            <GridContainer>
              {this.moreFields().map(this.renderField)}
            </GridContainer>
          ) : null}

          {missing.length > 0 ? (
            <div style={{ color: "#f44336", fontSize: 13, marginTop: 8 }}>
              Required: {missing.map(function(k) {
                var d = FIELD_DEFS.filter(function(x) { return x.key === k; })[0];
                return d ? d.label.toLowerCase() : k;
              }).join(", ")}
            </div>
          ) : null}
        </DialogContent>

        <DialogActions className={classes.modalFooter}>
          <Button onClick={this.props.onCancel} disabled={this.props.saving}>
            Cancel
          </Button>
          <Button
            color="rose"
            onClick={this.handleSave()}
            disabled={this.saveDisabled()}
          >
            {this.props.saving ? (
              <CircularProgress size={18} style={{ color: "#fff" }} />
            ) : (
              "Save"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const mapStateToProps = function(store) {
  return { activeDomain: store.login.user.activeDomain };
};

export default connect(mapStateToProps)(withStyles(modalStyle)(ReviewDialog));
